"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";

const API = "https://erp-xe9u.onrender.com/api";

const fmt = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtQty = (n) =>
  (n ?? 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const cls = (...c) => c.filter(Boolean).join(" ");

// Stable key: itemCode only — rate is now server-resolved, not part of identity
// Stable key: itemCode + bidArea — same code across different bid areas
// (work-type projects) must be addressable as distinct selectable rows.
const ITEM_KEY = (itemCode, bidArea) =>
  `${(itemCode || "").trim().toLowerCase()}||${(bidArea || "").trim().toLowerCase()}`;

const sectionLabel = (sn) => {
  if (!sn) return "";
  const idx = sn.indexOf(" - ");
  return idx !== -1 ? sn.slice(idx + 3) : sn;
};

// ── Rate source badge ──────────────────────────────────────────────────────
function RateSourceBadge({ source, small }) {
  if (!source || source === "none") return null;
  const map = {
    db: {
      label: "DB",
      classes: "bg-blue-900/50 text-blue-300 border-blue-700/50",
      title: "Rate from Item Database catalog",
    },
    budget: {
      label: "Budget",
      classes: "bg-amber-900/40 text-amber-300 border-amber-700/40",
      title: "Rate from budget row (no catalog entry found)",
    },
    manual: {
      label: "Manual",
      classes: "bg-purple-900/50 text-purple-300 border-purple-700/50",
      title: "Rate manually overridden",
    },
  };
  const cfg = map[source];
  if (!cfg) return null;
  return (
    <span
      title={cfg.title}
      className={cls(
        "inline-flex px-1.5 py-0.5 font-bold rounded border tracking-wide",
        small ? "text-[8px]" : "text-[9px]",
        cfg.classes,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ── Availability bar ───────────────────────────────────────────────────────
function AvailBar({ estimated, installed, pending, small }) {
  const remaining = Math.max(
    0,
    (estimated || 0) - (installed || 0) - (pending || 0),
  );
  const pct = estimated > 0 ? Math.min(100, (remaining / estimated) * 100) : 0;
  const color =
    pct >= 60 ? "bg-green-500" : pct >= 20 ? "bg-yellow-400" : "bg-red-400";
  const textColor =
    pct >= 60
      ? "text-green-400"
      : pct >= 20
        ? "text-yellow-400"
        : "text-red-400";
  return (
    <div className="flex items-center gap-1.5">
      <div
        className={cls(
          "flex-1 bg-gray-700 rounded-full overflow-hidden",
          small ? "h-1" : "h-1.5",
        )}
      >
        <div
          className={cls("h-full rounded-full transition-all", color)}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span
        className={cls(
          "shrink-0 font-mono tabular-nums",
          textColor,
          small ? "text-[9px]" : "text-[10px]",
        )}
      >
        {fmtQty(remaining)} left
      </span>
    </div>
  );
}

// ── Selected item row (compact tray) ──────────────────────────────────────
function SelectedRow({ item, qty, rate, onQtyChange, onRateChange, onRemove }) {
  const lineTotal = qty * rate;
  const rateIsEdited = Math.abs(rate - item.unitRate) > 0.001;
  const displaySource = rateIsEdited ? "manual" : item.rateSource;

  return (
    <div className="flex items-start gap-3 px-4 py-2.5 border-b border-gray-700/40 last:border-b-0 group">
      {/* Identity */}
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-mono text-orange-400">
            {item.itemCode}
          </span>
          <RateSourceBadge source={displaySource} small />
          {item.hasCoAddition && (
            <span className="text-[9px] bg-purple-800/50 text-purple-300 px-1 py-0.5 rounded font-semibold">
              CO +{fmtQty(item.coDeltaQty)}
            </span>
          )}
          <span className="text-xs text-gray-300 truncate">
            {item.description}
          </span>
        </div>
        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
          <span className="text-[10px] text-gray-500">
            {item.uom || "unit"}
          </span>
          {item.sectionNumber && (
            <span className="text-[9px] text-indigo-400">
              {sectionLabel(item.sectionNumber) || item.sectionNumber}
            </span>
          )}
          {item.bidArea && (
            <span className="text-[9px] text-gray-500">{item.bidArea}</span>
          )}
          {item.budgetRate &&
            item.rateSource === "db" &&
            Math.abs(item.budgetRate - item.unitRate) > 0.01 && (
              <span
                className="text-[9px] text-gray-600"
                title="Prorated budget rate"
              >
                budget: ${fmt(item.budgetRate)}
              </span>
            )}
        </div>
      </div>

      {/* Avail bar */}
      {item.qtyEstimated > 0 && (
        <div className="shrink-0 w-28 hidden sm:block pt-2">
          <AvailBar
            estimated={item.qtyEstimated}
            installed={item.qtyInstalled}
            pending={item.qtyPending}
            small
          />
        </div>
      )}

      {/* Line total */}
      <div className="shrink-0 w-20 text-right hidden md:block pt-2">
        <span className="text-xs font-mono text-orange-400">
          ${fmt(lineTotal)}
        </span>
      </div>

      {/* Rate input */}
      <div className="shrink-0 flex flex-col items-end gap-0.5">
        <div className="flex items-center gap-1">
          <span className="text-gray-500 text-[10px]">$</span>
          <input
            type="number"
            min="0"
            step="0.01"
            value={rate === 0 ? "" : rate}
            onChange={(e) => onRateChange(parseFloat(e.target.value) || 0)}
            className={cls(
              "w-20 px-2 py-1.5 text-xs text-right bg-gray-700 border rounded font-mono focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
              rateIsEdited
                ? "border-purple-500/70 text-purple-200"
                : "border-gray-600 text-gray-300 focus:border-orange-500",
            )}
          />
        </div>
        <span className="text-[9px] text-gray-600">/{item.uom || "unit"}</span>
      </div>

      {/* Qty input */}
      <div className="shrink-0">
        <input
          type="number"
          min="0"
          value={qty || ""}
          onChange={(e) => onQtyChange(parseFloat(e.target.value) || 0)}
          placeholder="0"
          className="w-24 px-2 py-1.5 text-sm text-right bg-gray-700 border border-orange-500/60 rounded font-mono text-orange-200 focus:outline-none focus:border-orange-500 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </div>

      {/* Remove */}
      <button
        onClick={onRemove}
        className="shrink-0 w-6 h-6 flex items-center justify-center text-gray-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 mt-1"
        title="Remove"
      >
        ×
      </button>
    </div>
  );
}

// ── Search result row ──────────────────────────────────────────────────────
function SearchResultRow({ item, qty, rate, onQtyChange, onRateChange }) {
  const hasQty = qty > 0;
  const rateIsEdited = Math.abs(rate - item.unitRate) > 0.001;
  const displaySource = rateIsEdited ? "manual" : item.rateSource;

  return (
    <div
      className={cls(
        "flex items-start gap-3 px-4 py-3 border-b border-gray-700/30 transition-colors cursor-default",
        hasQty
          ? "bg-orange-900/10 border-l-2 border-orange-500/60"
          : "border-l-2 border-transparent",
      )}
    >
      {/* Identity */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
          <span className="text-[11px] font-mono text-orange-400 shrink-0">
            {item.itemCode}
          </span>
          <RateSourceBadge source={displaySource} small />
          {item.sectionNumber && (
            <span className="text-[9px] font-mono text-indigo-400 bg-indigo-900/40 px-1 py-0.5 rounded">
              {sectionLabel(item.sectionNumber) || item.sectionNumber}
            </span>
          )}
          {item.hasCoAddition && (
            <span className="text-[9px] bg-purple-800/50 text-purple-300 px-1 py-0.5 rounded font-semibold">
              CO +{fmtQty(item.coDeltaQty)}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-200 truncate">{item.description}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">
          {item.uom}
          {item.bidArea ? ` · ${item.bidArea}` : ""}
          {item.budgetRate &&
            item.rateSource === "db" &&
            Math.abs(item.budgetRate - item.unitRate) > 0.01 && (
              <span
                className="ml-2 text-gray-600"
                title="Original prorated budget rate"
              >
                budget rate: ${fmt(item.budgetRate)}
              </span>
            )}
        </p>
      </div>

      {/* Rate + avail */}
      <div className="shrink-0 w-44 space-y-1 pt-0.5">
        <div className="flex flex-col items-end gap-0.5">
          <div className="flex items-center gap-1">
            <span className="text-gray-500 text-[10px]">$</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={rate === 0 ? "" : rate}
              onChange={(e) => onRateChange(parseFloat(e.target.value) || 0)}
              className={cls(
                "w-24 px-2 py-1 text-xs text-right bg-gray-700 border rounded font-mono focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
                rateIsEdited
                  ? "border-purple-500/70 text-purple-200"
                  : "border-gray-600 text-gray-300 focus:border-orange-500",
              )}
            />
          </div>
          <RateSourceBadge source={displaySource} small />
        </div>
        {item.qtyEstimated > 0 ? (
          <div>
            <div className="flex justify-between mb-0.5">
              <span className="text-[9px] text-gray-600">qty avail</span>
              <span className="text-[9px] text-gray-600 font-mono">
                {fmtQty(item.qtyEstimated)} est.
              </span>
            </div>
            <AvailBar
              estimated={item.qtyEstimated}
              installed={item.qtyInstalled}
              pending={item.qtyPending}
              small
            />
          </div>
        ) : (
          <p className="text-[9px] text-gray-600 text-right">No budget qty</p>
        )}
      </div>

      {/* Qty input */}
      <div className="shrink-0 w-28 text-right">
        <input
          type="number"
          min="0"
          value={qty || ""}
          onChange={(e) => onQtyChange(parseFloat(e.target.value) || 0)}
          placeholder="0"
          className={cls(
            "w-full px-2 py-1.5 text-sm text-right bg-gray-700 border rounded font-mono focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
            hasQty
              ? "border-orange-500 text-orange-200"
              : "border-gray-600 text-gray-200 focus:border-orange-500",
          )}
        />
        {hasQty && (
          <p className="text-[9px] text-orange-400 font-mono mt-0.5">
            ${fmt(qty * rate)}
          </p>
        )}
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function CreatePieceworkerLog3() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const stateProjectId = location.state?.projectId || params.id || "";
  const stateWoId = location.state?.workOrderId || "";
  const stateWoTitle = location.state?.workOrderTitle || "";

  const [projects, setProjects] = useState([]);
  const [workOrders, setWorkOrders] = useState([]);
  const [workers, setWorkers] = useState([]);

  const [selectedProjectId, setSelectedProjectId] = useState(stateProjectId);
  const [selectedWoId, setSelectedWoId] = useState(stateWoId);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  const [workCompletedDate, setWorkCompletedDate] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [notes, setNotes] = useState("");

  // budgetGroups from /available-items — already rate-resolved by server
  const [budgetGroups, setBudgetGroups] = useState([]);

  const [loadingWOs, setLoadingWOs] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // qtys: itemCode → number
  const [qtys, setQtys] = useState({});
  // rates: itemCode → number (overrides server-resolved rate when set manually)
  const [rates, setRates] = useState({});

  const [search, setSearch] = useState("");
  const [sectionFilter, setSectionFilter] = useState("all");

  // ── Data loading ──────────────────────────────────────────────────────────
  useEffect(() => {
    Promise.all([
      axios.get(`${API}/projects`, { headers }),
      axios.get(`${API}/users/pieceworkers`, { headers }),
    ])
      .then(([pRes, wRes]) => {
        setProjects(pRes.data.projects || pRes.data || []);
        setWorkers(wRes.data || []);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!selectedProjectId) {
      setWorkOrders([]);
      return;
    }
    setLoadingWOs(true);
    axios
      .get(`${API}/workorders3/project/${selectedProjectId}`, { headers })
      .then((r) =>
        setWorkOrders(
          (r.data.workOrders || []).filter((wo) =>
            ["piecework", "union_piecework"].includes(wo.type),
          ),
        ),
      )
      .catch(console.error)
      .finally(() => setLoadingWOs(false));
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedWoId) {
      setBudgetGroups([]);
      setQtys({});
      setRates({});
      setSectionFilter("all");
      setSearch("");
      return;
    }
    setLoadingItems(true);
    setError("");
    axios
      .get(
        `${API}/pieceworkerLogs3/work-order/${selectedWoId}/available-items`,
        { headers },
      )
      .then((r) => {
        const groups = r.data.budgetGroups || [];
        setBudgetGroups(groups);
        // Init rates map from server-resolved rates
        const initRates = {};
        groups.forEach((g) => {
          initRates[ITEM_KEY(g.itemCode, g.bidArea)] = g.unitRate || 0;
        });
        setRates(initRates);
        setQtys({});
        setSectionFilter("all");
        setSearch("");
      })
      .catch((e) => setError(e.response?.data?.message || e.message))
      .finally(() => setLoadingItems(false));
  }, [selectedWoId]);

  // ── Items list (already server-resolved, just sort) ───────────────────────
  const allItems = useMemo(() => {
    return [...budgetGroups].sort((a, b) => {
      const s = (a.sectionNumber || "").localeCompare(b.sectionNumber || "");
      return s !== 0 ? s : (a.itemCode || "").localeCompare(b.itemCode || "");
    });
  }, [budgetGroups]);

  // ── Selected items ────────────────────────────────────────────────────────
  const selectedItems = useMemo(
    () =>
      allItems.filter((i) => (qtys[ITEM_KEY(i.itemCode, i.bidArea)] || 0) > 0),
    [allItems, qtys],
  );

  // ── Search results ────────────────────────────────────────────────────────
  const isSearching = search.trim().length > 0 || sectionFilter !== "all";

  const searchResults = useMemo(() => {
    if (!isSearching) return [];
    const q = search.toLowerCase();
    return allItems.filter((item) => {
      if (
        sectionFilter !== "all" &&
        (item.sectionNumber || "") !== sectionFilter
      )
        return false;
      if (
        q &&
        !item.itemCode.toLowerCase().includes(q) &&
        !(item.description || "").toLowerCase().includes(q)
      )
        return false;
      return true;
    });
  }, [allItems, search, sectionFilter, isSearching]);

  const uniqueSections = useMemo(() => {
    const secs = new Set(allItems.map((i) => i.sectionNumber).filter(Boolean));
    return ["all", ...[...secs].sort()];
  }, [allItems]);

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => {
    let lineTotal = 0;
    let manualCount = 0;
    let budgetRateCount = 0;
    selectedItems.forEach((item) => {
      const key = ITEM_KEY(item.itemCode, item.bidArea);
      const qty = qtys[key] || 0;
      const rate = rates[key] ?? item.unitRate;
      lineTotal += qty * rate;
      const rateChanged = Math.abs(rate - item.unitRate) > 0.001;
      if (rateChanged) manualCount++;
      else if (item.rateSource === "budget") budgetRateCount++;
    });
    return {
      lineTotal,
      totalCount: selectedItems.length,
      manualCount,
      budgetRateCount,
    };
  }, [selectedItems, qtys, rates]);

  const setQty = (itemCode, bidArea, v) =>
    setQtys((p) => ({
      ...p,
      [ITEM_KEY(itemCode, bidArea)]: v > 0 ? v : 0,
    }));

  const setRate = (itemCode, bidArea, v) =>
    setRates((p) => ({ ...p, [ITEM_KEY(itemCode, bidArea)]: v }));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError("");
    if (!selectedWoId) {
      setError("Select a work order.");
      return;
    }
    if (!selectedWorkerId) {
      setError("Select a worker.");
      return;
    }

    const lineItems = selectedItems.map((item) => {
      const key = ITEM_KEY(item.itemCode, item.bidArea);
      const qty = qtys[key] || 0;
      const resolvedRate = rates[key] ?? item.unitRate;
      const rateChanged = Math.abs(resolvedRate - item.unitRate) > 0.001;
      const rateSource = rateChanged ? "manual" : item.rateSource || "db";
      return {
        budgetItemId: item.primaryParentId || null,
        itemCode: item.itemCode,
        description: item.description,
        sectionNumber: item.sectionNumber || "",
        bidArea: item.bidArea || "",
        uom: item.uom || "",
        unitRate: resolvedRate,
        rateSource,
        quantity: qty,
        laborHours: item.laborHours || 0,
      };
    });

    if (!lineItems.length) {
      setError("Enter quantity for at least one item.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/pieceworkerLogs3`,
        {
          projectId: selectedProjectId,
          workOrderId: selectedWoId,
          workerId: selectedWorkerId,
          lineItems,
          workCompletedDate,
          notes,
        },
        { headers },
      );
      navigate(`/Projects/${selectedProjectId}?tab=Worksheets`);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const selectedWo = workOrders.find((w) => w._id === selectedWoId);

  return (
    <div className="mx-auto px-4 py-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() =>
            selectedProjectId
              ? navigate(`/Projects/${selectedProjectId}?tab=Worksheets`)
              : navigate(-1)
          }
          className="px-3 py-1.5 text-sm text-gray-400 hover:text-white bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
        >
          ← Back
        </button>
        <h1 className="text-lg font-bold text-white">New Piecework Log</h1>
        {stateWoTitle && (
          <span className="text-sm text-gray-400">— {stateWoTitle}</span>
        )}
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-900/40 border border-red-700 rounded-lg text-sm text-red-300">
          {error}
        </div>
      )}

      {/* ── Meta fields ── */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Project
          </label>
          <select
            value={selectedProjectId}
            onChange={(e) => {
              setSelectedProjectId(e.target.value);
              setSelectedWoId("");
            }}
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          >
            <option value="">Select project…</option>
            {projects.map((p) => (
              <option key={p._id} value={p._id}>
                {p.projectName || p.projectNumber}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Work Order
          </label>
          <select
            value={selectedWoId}
            onChange={(e) => setSelectedWoId(e.target.value)}
            disabled={!selectedProjectId}
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none disabled:opacity-50"
          >
            <option value="">Select work order…</option>
            {workOrders.map((wo) => (
              <option key={wo._id} value={wo._id}>
                {wo.woNumber} — {wo.title}
              </option>
            ))}
          </select>
          {selectedWo && (
            <p className="text-[10px] text-gray-500 mt-1">
              {selectedWo.bidArea && `Floor: ${selectedWo.bidArea} · `}
              <span
                className={cls(
                  "font-semibold",
                  selectedWo.type === "union_piecework"
                    ? "text-teal-400"
                    : "text-orange-400",
                )}
              >
                {selectedWo.type === "union_piecework"
                  ? "Union Piecework"
                  : "Piecework"}
              </span>
              {selectedWo.union &&
                selectedWo.union !== "non-union" &&
                ` · ${selectedWo.union}`}
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Worker
          </label>
          <select
            value={selectedWorkerId}
            onChange={(e) => setSelectedWorkerId(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          >
            <option value="">Select worker…</option>
            {workers.map((w) => (
              <option key={w._id} value={w._id}>
                {w.displayName || w.email}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Work Completed Date
          </label>
          <input
            type="date"
            value={workCompletedDate}
            onChange={(e) => setWorkCompletedDate(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 focus:border-orange-500 focus:outline-none"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs text-gray-400 mb-1.5 uppercase tracking-wide">
            Notes
          </label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes…"
            className="w-full px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
          />
        </div>
      </div>

      {/* ── Items panel ── */}
      {selectedWoId && (
        <>
          {loadingItems ? (
            <div className="flex items-center justify-center py-12 bg-gray-800 border border-gray-700 rounded-xl">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
              <span className="ml-3 text-sm text-gray-400">Loading items…</span>
            </div>
          ) : (
            <div className="space-y-3">
              {/* ── Selected items tray ── */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                <div className="px-5 py-3 border-b border-gray-700 flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                    <h2 className="text-sm font-semibold text-white">
                      Selected Items
                    </h2>
                    {totals.totalCount > 0 && (
                      <span className="px-2 py-0.5 text-[10px] font-bold bg-orange-500/20 text-orange-300 rounded-full border border-orange-500/30">
                        {totals.totalCount}
                      </span>
                    )}
                  </div>

                  {/* Rate legend */}
                  <div className="flex items-center gap-3 text-[10px] text-gray-600">
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-400 inline-block" />
                      <span className="text-blue-400">DB</span> = catalog rate
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />
                      <span className="text-amber-400">Budget</span> = prorated
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-purple-400 inline-block" />
                      <span className="text-purple-400">Manual</span> = override
                    </span>
                  </div>

                  <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
                    {totals.budgetRateCount > 0 && (
                      <span className="text-amber-400 text-[10px]">
                        {totals.budgetRateCount} using prorated budget rate
                      </span>
                    )}
                    {totals.manualCount > 0 && (
                      <span className="text-purple-400 text-[10px]">
                        {totals.manualCount} manually overridden
                      </span>
                    )}
                    <span className="font-mono font-bold text-orange-400 text-sm">
                      ${fmt(totals.lineTotal)}
                      <span className="text-gray-500 text-[10px] font-normal ml-1">
                        incl. burden
                      </span>
                    </span>
                  </div>
                </div>

                {selectedItems.length === 0 ? (
                  <div className="px-5 py-6 text-center">
                    <p className="text-sm text-gray-500">
                      No items selected yet.
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Search below to find and add labour items.
                    </p>
                  </div>
                ) : (
                  <div>
                    {/* Tray column headers */}
                    <div className="px-4 py-1.5 bg-gray-900/40 border-b border-gray-700/50 flex items-center gap-3 text-[10px] text-gray-500 uppercase tracking-wide">
                      <span className="flex-1">Item</span>
                      <span className="shrink-0 w-28 hidden sm:block">
                        Available
                      </span>
                      <span className="shrink-0 w-20 text-right hidden md:block">
                        Line Total
                      </span>
                      <span className="shrink-0 w-24 text-right">Rate</span>
                      <span className="shrink-0 w-24 text-right">Qty</span>
                      <span className="shrink-0 w-6" />
                    </div>
                    {selectedItems.map((item) => {
                      const key = ITEM_KEY(item.itemCode, item.bidArea);
                      return (
                        <SelectedRow
                          key={key}
                          item={item}
                          qty={qtys[key] || 0}
                          rate={rates[key] ?? item.unitRate}
                          onQtyChange={(v) =>
                            setQty(item.itemCode, item.bidArea, v)
                          }
                          onRateChange={(v) =>
                            setRate(item.itemCode, item.bidArea, v)
                          }
                          onRemove={() =>
                            setQty(item.itemCode, item.bidArea, 0)
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ── Search panel ── */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
                {/* Search bar */}
                <div className="px-4 py-3 border-b border-gray-700 flex flex-wrap items-center gap-2 bg-gray-900/20">
                  <div className="relative flex-1 min-w-[200px]">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">
                      ⌕
                    </span>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search by code or description…"
                      className="w-full pl-8 pr-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-200 placeholder-gray-500 focus:border-orange-500 focus:outline-none"
                      autoComplete="off"
                    />
                    {search && (
                      <button
                        onClick={() => setSearch("")}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 text-lg leading-none"
                      >
                        ×
                      </button>
                    )}
                  </div>
                  <select
                    value={sectionFilter}
                    onChange={(e) => setSectionFilter(e.target.value)}
                    className="px-3 py-2 text-sm bg-gray-700 border border-gray-600 rounded-lg text-gray-300 focus:border-orange-500 focus:outline-none"
                  >
                    {uniqueSections.map((s) => (
                      <option key={s} value={s}>
                        {s === "all" ? "All Sections" : sectionLabel(s) || s}
                      </option>
                    ))}
                  </select>
                  {isSearching && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {searchResults.length} result
                      {searchResults.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>

                {/* Results */}
                {!isSearching ? (
                  <div className="px-5 py-8 text-center">
                    <p className="text-sm text-gray-500">
                      {allItems.length > 0
                        ? `${allItems.length} items available — search or filter to add`
                        : "No labour items found for this work order."}
                    </p>
                    {allItems.length === 0 && (
                      <p className="text-xs text-gray-600 mt-1">
                        Add labor items to the Item Database (category L) or
                        upload a budget with labour lines.
                      </p>
                    )}
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="px-5 py-8 text-center text-gray-500 text-sm">
                    No items match.
                  </div>
                ) : (
                  <div>
                    {searchResults.map((item) => {
                      const key = ITEM_KEY(item.itemCode, item.bidArea);
                      return (
                        <SearchResultRow
                          key={key}
                          item={item}
                          qty={qtys[key] || 0}
                          rate={rates[key] ?? item.unitRate}
                          onQtyChange={(v) =>
                            setQty(item.itemCode, item.bidArea, v)
                          }
                          onRateChange={(v) =>
                            setRate(item.itemCode, item.bidArea, v)
                          }
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {!selectedWoId && selectedProjectId && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl py-12 text-center">
          <p className="text-gray-500 text-sm">
            Select a work order above to load labour items.
          </p>
        </div>
      )}
      {!selectedWoId && !selectedProjectId && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl py-12 text-center">
          <p className="text-gray-500 text-sm">
            Select a project and work order above to get started.
          </p>
        </div>
      )}

      {/* ── Footer ── */}
      {selectedWoId && (
        <div className="flex items-center justify-between flex-wrap gap-4 pt-2 border-t border-gray-700">
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                Items selected
              </p>
              <p className="font-mono text-white">
                {totals.totalCount}
                {totals.manualCount > 0 && (
                  <span className="text-purple-400 ml-2 text-xs">
                    ({totals.manualCount} manual rate)
                  </span>
                )}
                {totals.budgetRateCount > 0 && (
                  <span className="text-amber-400 ml-2 text-xs">
                    ({totals.budgetRateCount} budget rate)
                  </span>
                )}
              </p>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-0.5">
                Total Labour Cost
              </p>
              <p className="font-mono font-bold text-orange-400 text-lg">
                ${fmt(totals.lineTotal)}
              </p>
              <p className="text-[10px] text-gray-600">
                Incl. burden (server-calculated)
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={() =>
                selectedProjectId
                  ? navigate(`/Projects/${selectedProjectId}?tab=Worksheets`)
                  : navigate(-1)
              }
              className="px-4 py-2 text-sm text-gray-300 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || totals.totalCount === 0}
              className={cls(
                "px-6 py-2 text-sm font-semibold text-white rounded-lg transition-colors",
                submitting || totals.totalCount === 0
                  ? "bg-gray-600 cursor-not-allowed"
                  : "bg-orange-500 hover:bg-orange-600",
              )}
            >
              {submitting ? "Submitting…" : "Submit Log"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
